/**
 * A fabricated zero is the same class of lie the audit trail exists to prevent (§21's own
 * checkpoint): every formatter here renders "—" for null/undefined rather than 0, and the
 * dashboard renders `live: false` as an explicit "broker not connected" state rather than
 * an empty account.
 */
const DASH = "—";

export function formatMoney(value: string | number | null | undefined, currency = "USD"): string {
  if (value === null || value === undefined) return DASH;
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return DASH;
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    // The server answers "" when no account snapshot has ever been taken, and `Intl` throws
    // a RangeError on an empty currency code — a whole screen lost to a figure we do have.
    currency: currency || "USD",
    signDisplay: "exceptZero",
  }).format(n);
  return formatted;
}

export function formatPips(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return DASH;
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return DASH;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)} pips`;
}

/**
 * The most decimals any instrument this broker quotes actually carries. Beyond this a digit
 * is IEEE-754 dust, not price: the broker's float reaches the ws tick untouched
 * (`metaapi/listener.ts` -> `live.ts`), so `String(n)` rendered `2405.1000000000004` in a
 * `font-mono` cell and reflowed the card until the next tick landed. Five is the usual FX
 * maximum and eight leaves room for a crypto pair without ever reaching dust territory.
 */
const MAX_PRICE_DECIMALS = 8;

/**
 * `digits` is the instrument's own precision from the symbol spec (`BrokerSymbol.digits`).
 * Pass it wherever it is known — a fixed width is what stops a cell changing size as the
 * tick moves, which no amount of dust-stripping can do on its own. Absent, the value is
 * rounded to `MAX_PRICE_DECIMALS` and trailing zeros dropped, which leaves every genuine
 * quote untouched and collapses only the dust.
 *
 * Deliberately `number | null | undefined`: the symbols query answers `digits: null` for an
 * instrument it has no spec for, and 0 is a real digit count for an index — so the check is
 * `typeof === "number"`, never truthiness.
 */
export function formatPrice(
  value: string | number | null | undefined,
  digits?: number | null,
): string {
  if (value === null || value === undefined) return DASH;
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return DASH;
  if (typeof digits === "number" && Number.isFinite(digits)) {
    // Clamped because a bad spec must not reach toFixed, which throws outside 0-100 and
    // would take the whole card down over a cosmetic figure.
    return n.toFixed(Math.min(Math.max(Math.trunc(digits), 0), MAX_PRICE_DECIMALS));
  }
  return String(Number(n.toFixed(MAX_PRICE_DECIMALS)));
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return DASH;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function formatRelative(value: string | null | undefined): string {
  if (!value) return DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return DASH;
  const diffMs = d.getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (Math.abs(diffMin) < 60) return rtf.format(diffMin, "minute");
  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 24) return rtf.format(diffHr, "hour");
  return rtf.format(Math.round(diffHr / 24), "day");
}

/**
 * Uptime, from the server's own `uptimeSeconds` rather than from `startedAt` and the clock
 * on this device — a phone whose clock is off must not be able to change how long the box
 * says it has been up. That is also why this exists instead of reusing `formatRelative`.
 *
 * Coarse on purpose: past a day, nobody reads the minutes.
 */
export function formatUptime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return DASH;
  if (!Number.isFinite(seconds) || seconds < 0) return DASH;
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}
